const findAllByLocalName = (parent: Document | Element, localName: string): Element[] => {
  const results: Element[] = [];
  const allElements = parent.getElementsByTagName('*');
  for (let i = 0; i < allElements.length; i += 1) {
    const element = allElements[i];
    const tagLocalName = element.localName || element.tagName.split(':').pop();
    if (tagLocalName === localName) {
      results.push(element);
    }
  }
  return results;
};

const decodeBase64Pdf = (base64Content: string): Blob | null => {
  try {
    const binaryString = atob(base64Content);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i += 1) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new Blob([bytes], { type: 'application/pdf' });
  } catch {
    return null;
  }
};

/** Extract embedded PDF from UBL XML (Billit e-invoice). */
export const extractPdfFromUblXml = (ublXmlContent: string): Blob | null => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(ublXmlContent, 'text/xml');

  if (xmlDoc.querySelector('parsererror')) {
    return null;
  }

  const embeddedDocs = findAllByLocalName(xmlDoc, 'EmbeddedDocumentBinaryObject');
  const pdfDoc = embeddedDocs.find(embeddedDoc => {
    const mimeType =
      embeddedDoc.getAttribute('mimeCode') ||
      embeddedDoc.getAttribute('mimeType') ||
      '';
    const base64Content = embeddedDoc.textContent?.trim();
    return Boolean(base64Content && mimeType.toLowerCase().includes('pdf'));
  });

  if (!pdfDoc) {
    return null;
  }

  const base64Content = pdfDoc.textContent?.trim() || '';
  return decodeBase64Pdf(base64Content);
};

export const createPdfObjectUrlFromUblXml = (ublXmlContent: string): string | null => {
  const blob = extractPdfFromUblXml(ublXmlContent);
  if (!blob) {
    return null;
  }
  return URL.createObjectURL(blob);
};

export type EmbeddedUblPdf = {
  fileName: string;
  blob: Blob;
};

/**
 * Alle ingebedde PDF's uit de UBL, in volgorde. De eerste is de factuur;
 * sommige leveranciers (bv. Van Herck) sturen extra documenten mee (leveringsbonnen, voorwaarden).
 */
export const extractAllPdfsFromUblXml = (ublXmlContent: string): EmbeddedUblPdf[] => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(ublXmlContent, 'text/xml');
  if (xmlDoc.querySelector('parsererror')) return [];

  return findAllByLocalName(xmlDoc, 'EmbeddedDocumentBinaryObject')
    .filter(doc => {
      const mime = (doc.getAttribute('mimeCode') || doc.getAttribute('mimeType') || '').toLowerCase();
      const name = (doc.getAttribute('filename') || '').toLowerCase();
      return Boolean(doc.textContent?.trim()) && (mime.includes('pdf') || name.endsWith('.pdf'));
    })
    .map((doc, index) => ({
      fileName: doc.getAttribute('filename') || `document-${index + 1}.pdf`,
      blob: decodeBase64Pdf((doc.textContent || '').replace(/\s/g, '')),
    }))
    .filter((pdf): pdf is EmbeddedUblPdf => pdf.blob !== null);
};
